import { Trans, useLingui } from '@lingui/react/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { addTournamentUserToTeam } from 'modules/players/store/actions';
import { PlayerAsyncSelect, PlayerSelectOption } from 'modules/players/ui/components/player-async-select';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SingleValue } from 'react-select';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import { UDButton, UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { Link } from './styles';

interface IProps {
  teamId: number;
}

function useModalAddPlayer({ teamId }: IProps) {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();

  const [selectedOptions, setSelectedOptions] = useState<PlayerSelectOption | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedOptions(null);
    close();
  }, [close]);

  const selectedPlayer = selectedOptions?.value;

  const handleAddPlayer = useCallback(async () => {
    if (!selectedPlayer) return;

    try {
      await dispatch(addTournamentUserToTeam({ userId: selectedPlayer.id, teamId })).unwrap();
      // Состав команды перечитает getTeamById — thunk сбрасывает кэш по тегу Team.
      handleClose();
      toast.success(t`Игрок добавлен в команду`);
    } catch (error) {
      if (error === 'User is already in this team') {
        toast.warn(t`Игрок уже в команде`);
      } else {
        toast.error(t`Ошибка при добавлении игрока в команду`);
      }
    }
  }, [dispatch, handleClose, selectedPlayer, teamId, t]);

  const handleSelectPlayer = useCallback((newValue: SingleValue<PlayerSelectOption>) => {
    setSelectedOptions(newValue);
  }, []);

  const modal = (
    <Modal show={isOpen} onClose={handleClose} size="md">
      <UDText type="title-3" className="mb-4">
        <Trans>Добавить игрока из списка</Trans>
      </UDText>
      <Modal.Body>
        <PlayerAsyncSelect
          key={isOpen ? 'open' : 'closed'}
          autoFocus
          className="mb-7.5"
          onChange={handleSelectPlayer}
          value={selectedOptions}
        />
      </Modal.Body>
      <Modal.Footer className="flex flex-col">
        <UDButton type="button" stretch variant="primary" onClick={handleAddPlayer} disabled={!selectedPlayer}>
          <Trans>Добавить</Trans>
        </UDButton>
        <Link to={Pages.TEAMS.builders.edit(teamId, 'players/create')}>
          <Trans>Создать нового игрока</Trans>
        </Link>
      </Modal.Footer>
    </Modal>
  );

  return { open, Modal: modal };
}

export default useModalAddPlayer;
